import { m as motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { EXTERNAL_LINKS } from "@/lib/constants";
import {
  NEXT_COHORT_HERO_CTA,
  NEXT_COHORT_PATH,
} from "@/lib/cohorts";

export const HeroSection = () => {
  return (
    <section className="relative flex items-center overflow-hidden bg-gradient-mesh pt-24 md:pt-28 pb-20 md:pb-32">
      <div className="section-inner relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 md:gap-12 items-start">
          {/* Left Column — Text + CTA */}
          <div className="space-y-5 max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.15] md:leading-[1.1] gradient-text"
            >
              The only AI&nbsp;program where HR and Talent leaders build, not just learn.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="text-base md:text-lg lg:text-xl text-foreground/80 max-w-2xl leading-relaxed"
            >
              In 5 weeks, you'll work with an AI engineer and an I/O Psychologist to build a working
              prototype for your real HR/Talent workflow — backed by the behavioral
              science of what makes adoption stick.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="flex flex-wrap items-center gap-3"
            >
              <span className="text-sm md:text-base font-semibold text-primary">
                200+ graduates from Duracell, Netflix, T-Mobile, Meijer, and more.
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="flex flex-col items-start gap-4"
            >
              <Link
                to={NEXT_COHORT_PATH}
                className="group inline-flex items-center gap-2 md:gap-3 px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-semibold rounded-2xl bg-primary text-primary-foreground shadow-primary hover:shadow-glow-primary transition-all duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {NEXT_COHORT_HERO_CTA}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a
                href={EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION}
                className="text-sm md:text-base font-medium text-foreground/70 hover:text-primary transition-colors underline underline-offset-4"
              >
                Or apply now →
              </a>
            </motion.div>
          </div>

          {/* Right Column — Video Testimonial */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden md:flex flex-col items-center gap-4 w-48 lg:w-56"
          >
            <div className="relative glass-card p-1.5 hover-lift w-full">
              <div className="relative overflow-hidden rounded-xl aspect-[9/16]">
                <iframe
                  src="https://player.vimeo.com/video/1137010622?badge=0&autopause=0&player_id=0&app_id=58479"
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  title="Video Testimonial"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">Carrie Ott-Holland, PhD</p>
              <p className="text-xs text-muted-foreground">People Research Leader</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
